import React from 'react';
import { Card } from 'primereact/card';
import { DataTable as PrimeDataTable, DataTableRowClickEvent, DataTableStateEvent } from 'primereact/datatable';
import { SortOrder } from 'primereact/api';
import { Column } from 'primereact/column';
import Input from './Input';
import { TableProps, TableColumn } from '../types/table.types';
import '../styles/table.css';

export function DataTable<T extends Record<string, any>>({
  data,
  columns,
  loading = false,
  paginator = true,
  rows = 20,
  rowsPerPageOptions = [5, 10, 20, 30],
  emptyMessage = 'No data found',
  showGridlines = true,
  stripedRows = true,
  size = 'small',
  tableStyle,
  className = '',
  onRowClick,
  rowClassName,
  globalFilterFields,
  onGlobalFilterChange,
  globalFilterValue = '',
  globalFilterPlaceholder = 'Search...',
  showGlobalFilter = true,
  sortField,
  sortOrder,
  onSort,
}: TableProps<T>) {
  const handleGlobalFilterChange = (value: string) => {
    onGlobalFilterChange?.(value);
  };

  const renderColumn = (column: TableColumn<T>) => {
    const columnProps = {
      field: column.field as string,
      header: column.header,
      sortable: column.sortable,
      body: column.body,
      style: column.style,
      className: column.className,
      showFilterMenu: false,
    };

    if (column.minWidth) {
      columnProps.style = { ...columnProps.style, minWidth: column.minWidth };
    }

    if (column.width) {
      columnProps.style = { ...columnProps.style, width: column.width };
    }

    return <Column key={String(column.field)} {...columnProps} />;
  };

  const getTableSize = () => {
    switch (size) {
      case 'small':
        return 'p-datatable-sm';
      case 'large':
        return 'p-datatable-lg';
      default:
        return '';
    }
  };

  return (
    <Card className={`shadow-sm border-0 ${className}`}>
      {/* Global Filter */}
      {showGlobalFilter && (
        <div className="mb-4">
          <div className="flex items-center space-x-4">
            <Input
              id="global-filter"
              value={globalFilterValue}
              onChange={handleGlobalFilterChange}
              placeholder={globalFilterPlaceholder}
              className="flex-1"
            />
          </div>
        </div>
      )}

      {/* Data Table */}
      <PrimeDataTable
        value={data}
        paginator={paginator}
        rows={rows}
        rowsPerPageOptions={rowsPerPageOptions}
        className={`${getTableSize()} custom-datatable`}
        emptyMessage={emptyMessage}
        stripedRows={stripedRows}
        loading={loading}
        tableStyle={tableStyle}
        onRowClick={(event: DataTableRowClickEvent) => onRowClick?.(event.data as T)}
        rowClassName={rowClassName}
        globalFilterFields={globalFilterFields as string[]}
        globalFilter={globalFilterValue}
        sortField={sortField as string}
        sortOrder={sortOrder}
        onSort={(event: DataTableStateEvent) => {
          if (event.sortField && event.sortOrder !== undefined && event.sortOrder !== null) {
            onSort?.(event.sortField as keyof T, event.sortOrder as SortOrder);
          }
        }}
        removableSort
        showGridlines={false}
        style={{
          '--p-datatable-header-border-color': '#e2e8f0',
          '--p-datatable-row-hover-bg': '#f1f5f9',
        } as React.CSSProperties}
      >
        {columns.map(renderColumn)}
      </PrimeDataTable>
    </Card>
  );
} 